import React, { useState, useEffect } from 'react';
import { ProductOption } from '../types';
import { apiService } from '../services/api';
import { useStocks } from '../hooks';
import { ProductOptionsManager } from './ProductOptionsManager';
import Modal from './Modal';
import './CommonOptionsManagement.css';

const CommonOptionsManagement: React.FC = () => {
    const [commonOptions, setCommonOptions] = useState<ProductOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingOption, setEditingOption] = useState<ProductOption | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const { data: stocks, loading: stocksLoading, refetch: refetchStocks } = useStocks();

    useEffect(() => {
        fetchCommonOptions();
    }, []);

    const fetchCommonOptions = async () => {
        try {
            setLoading(true);
            setError(null);
            const options = await apiService.getCommonOptions();
            setCommonOptions(options);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch common options');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        const newOption: ProductOption = {
            id: `common_${Date.now()}`,
            name: 'New Common Option',
            type: 'single',
            required: false,
            choices: [
                {
                    id: `choice_${Date.now()}`,
                    name: 'Choice 1',
                    extraCost: 0,
                    defaultSelected: false,
                    stockIds: []
                }
            ],
            isCommonOption: true,
            commonOptionSourceId: `common_${Date.now()}`
        };
        setEditingOption(newOption);
        setIsEditModalOpen(true);
    };

    const handleEdit = (option: ProductOption) => {
        setEditingOption({ ...option });
        setIsEditModalOpen(true);
    };

    const handleSave = async () => {
        if (!editingOption) return;

        try {
            setIsSaving(true);
            setError(null);

            const isNew = !commonOptions.find(opt => opt.id === editingOption.id);

            if (isNew) {
                await apiService.createCommonOption(editingOption);
                setSuccessMessage('Common option created successfully!');
            } else {
                await apiService.updateCommonOption(editingOption.id, editingOption);

                // Ask if they want to propagate changes
                const shouldPropagate = confirm(
                    'Do you want to apply these changes to all products using this common option?'
                );

                if (shouldPropagate) {
                    // Pass the updated option data to avoid KV eventual consistency issues
                    const result = await apiService.propagateCommonOption(editingOption.id, editingOption);
                    setSuccessMessage(
                        `Common option updated and propagated to ${result.updated} product(s): ${result.productNames.join(', ')}`
                    );
                } else {
                    setSuccessMessage('Common option updated successfully!');
                }
            }

            setTimeout(() => setSuccessMessage(null), 5000);
            setIsEditModalOpen(false);
            setEditingOption(null);
            fetchCommonOptions();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save common option');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (option: ProductOption) => {
        try {
            // Check usage first
            const usage = await apiService.getCommonOptionUsage(option.id);

            if (usage.count > 0) {
                const confirmMessage = `This common option is used by ${usage.count} product(s):\n${usage.productNames.join(', ')}\n\nDelete anyway? (This will NOT affect existing products)`;
                if (!confirm(confirmMessage)) {
                    return;
                }
            } else {
                if (!confirm(`Delete common option "${option.name}"?`)) {
                    return;
                }
            }

            const result = await apiService.deleteCommonOption(option.id);

            if (result.success) {
                setSuccessMessage('Common option deleted successfully!');
                setTimeout(() => setSuccessMessage(null), 3000);
                fetchCommonOptions();
            } else {
                setError(result.message);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete common option');
        }
    };

    const handleCancel = () => {
        setIsEditModalOpen(false);
        setEditingOption(null);
    };

    if (loading) {
        return (
            <div className="common-options-management">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading common options...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="common-options-management">
            <div className="common-options-header">
                <div>
                    <h1>🔗 Common Options</h1>
                    <p className="subtitle">Centrally manage shared product options</p>
                </div>
                <button onClick={handleCreate} className="button primary">
                    ➕ Create Common Option
                </button>
            </div>

            {successMessage && (
                <div className="success-message">
                    <span className="success-icon">✅</span>
                    {successMessage}
                </div>
            )}

            {error && (
                <div className="error-message">
                    <span className="error-icon">❌</span>
                    {error}
                </div>
            )}

            {commonOptions.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">🔗</div>
                    <h3>No Common Options Yet</h3>
                    <p>Create reusable options that can be shared across multiple products</p>
                    <button onClick={handleCreate} className="button primary">
                        ➕ Create Common Option
                    </button>
                </div>
            ) : (
                <div className="common-options-grid">
                    {commonOptions.map(option => (
                        <div key={option.id} className="common-option-card">
                            <div className="card-header">
                                <h3>{option.name}</h3>
                                <span className="option-badge">{option.type}</span>
                            </div>

                            <div className="card-body">
                                <div className="option-meta">
                                    <span className="meta-item">
                                        {option.required ? '⚠️ Required' : '✓ Optional'}
                                    </span>
                                    <span className="meta-item">
                                        {option.choices.length} choice(s)
                                    </span>
                                </div>

                                <div className="option-choices">
                                    {option.choices.map((choice) => (
                                        <div key={choice.id} className="choice-item">
                                            <span className="choice-name">{choice.name}</span>
                                            {choice.extraCost > 0 && (
                                                <span className="choice-cost">+£{choice.extraCost.toFixed(2)}</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="card-actions">
                                <button
                                    onClick={() => handleEdit(option)}
                                    className="button secondary"
                                >
                                    ✏️ Edit
                                </button>
                                <button
                                    onClick={() => handleDelete(option)}
                                    className="button danger"
                                >
                                    🗑️ Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Modal
                isOpen={isEditModalOpen}
                onClose={handleCancel}
                showCloseButton={true}
            >
                <div className="common-option-editor">
                    <h2>{editingOption && commonOptions.find(opt => opt.id === editingOption.id) ? 'Edit' : 'Create'} Common Option</h2>

                    {editingOption && (
                        <ProductOptionsManager
                            options={[editingOption]}
                            onChange={(opts) => setEditingOption(opts[0])}
                            stocks={stocks || []}
                            stocksLoading={stocksLoading}
                            onRefreshStocks={refetchStocks}
                            disabled={isSaving}
                            hideCommonOptions={true}
                        />
                    )}

                    <div className="modal-actions">
                        <button
                            onClick={handleCancel}
                            className="button secondary"
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            className="button primary"
                            disabled={isSaving}
                        >
                            {isSaving ? 'Saving...' : 'Save Common Option'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default CommonOptionsManagement;
